import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";

function SelenseAuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const normalizedEmail = email.trim();

    if (!normalizedEmail || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const auth = getAuth();

      if (mode === "signup") {
        await createUserWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );
      } else {
        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );
      }
    } catch (err) {
      setError(err?.message || "Unable to authenticate.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    if (isSubmitting) {
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();

      await signInWithPopup(auth, provider);
    } catch (err) {
      setError(err?.message || "Unable to continue with Google.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function toggleMode() {
    setError("");
    setMode((currentMode) =>
      currentMode === "signin" ? "signup" : "signin"
    );
  }

  if (loading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (user) {
    return children;
  }

  const isSignUp = mode === "signup";

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={8}
    >
      <Box
        width="100%"
        maxW="440px"
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        px={{ base: 6, md: 8 }}
        py={{ base: 8, md: 10 }}
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Text
              fontSize="sm"
              fontWeight="700"
              color="teal.600"
              mb={3}
              letterSpacing="wide"
            >
              SELENSE
            </Text>

            <Heading
              size="lg"
              color="gray.800"
              lineHeight="1.25"
            >
              Keep the health story of someone you love in one place.
            </Heading>

            <Text
              mt={3}
              fontSize="sm"
              color="gray.600"
              lineHeight="1.6"
            >
              {isSignUp
                ? "Create your Selense health memory and start with one person you care for."
                : "Sign in to continue with the health memory you have already created."}
            </Text>
          </Box>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{error}</Text>
            </Alert>
          )}

          <Box as="form" onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">
                  Email
                </FormLabel>

                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  isDisabled={isSubmitting}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">
                  Password
                </FormLabel>

                <Input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Your password"
                  autoComplete={
                    isSignUp ? "new-password" : "current-password"
                  }
                  isDisabled={isSubmitting}
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="teal"
                size="lg"
                isLoading={isSubmitting}
                loadingText={
                  isSignUp ? "Creating account..." : "Signing in..."
                }
              >
                {isSignUp
                  ? "Create my health memory"
                  : "Sign in"}
              </Button>
            </VStack>
          </Box>

          <Button
            variant="outline"
            size="lg"
            onClick={handleGoogleSignIn}
            isLoading={isSubmitting}
            loadingText="Connecting..."
          >
            Continue with Google
          </Button>

          <Box textAlign="center">
            <Text fontSize="sm" color="gray.600">
              {isSignUp
                ? "Already have an account?"
                : "Need a Selense account?"}
            </Text>

            <Button
              variant="link"
              colorScheme="teal"
              mt={1}
              onClick={toggleMode}
              isDisabled={isSubmitting}
            >
              {isSignUp
                ? "Sign in"
                : "Create one"}
            </Button>
          </Box>

          <HStack justify="center" spacing={4}>
            <Link
              href="/privacy-policy"
              color="teal.600"
              fontWeight="600"
              _hover={{ textDecoration: "none", color: "teal.500" }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/privacy-policy#faq"
              color="teal.600"
              fontWeight="600"
              _hover={{ textDecoration: "none", color: "teal.500" }}
            >
              FAQs
            </Link>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}

export default SelenseAuthGate;